class LocationSDK {
  // Helper to normalize positional args or an options object for methods.
  static _normalizeOptions(args, shape) {
    // args: array-like arguments passed into the public method
    // shape: array of keys in order for positional mapping
    if (
      args.length === 1 &&
      args[0] &&
      typeof args[0] === "object" &&
      !Array.isArray(args[0])
    ) {
      return args[0];
    }
    const out = {};
    for (let i = 0; i < shape.length; i++) {
      out[shape[i]] = args[i] !== undefined ? args[i] : null;
    }
    return out;
  }

  /**
   * Checks if the current environment supports secure geolocation.
   * @returns {boolean}
   */
  static isSecureContext() {
    if (typeof window === "undefined" || typeof location === "undefined")
      return false;
    return (
      (window.isSecureContext && window.isSecureContext === true) ||
      location.protocol === "https:" ||
      location.hostname === "localhost"
    );
  }

  /**
   * Gets location from IP address using free IP geolocation services.
   * Tries multiple services as fallbacks for reliability.
   * @returns {Promise<Object>} - A promise that resolves to location data from IP.
   */
  static async getLocationFromIP() {
    // Client-side IP geolocation is disabled for local testing.
    // This method is intentionally stubbed so the SDK does not perform
    // IP -> location lookups in the browser during local tests.
    console.warn(
      "LocationSDK.getLocationFromIP() is stubbed out for local testing.",
    );
    return null;
  }

  /**
   * Gets the client's public IP address (string) using simple public IP endpoints.
   * This only returns the IP address (no coordinates). Used to forward `client_ip`
   * to the server when GPS is unavailable.
   */
  static async getClientIP() {
    const services = [
      {
        name: "ipify",
        url: "https://api.ipify.org?format=json",
        parser: (d) => d && d.ip,
      },
      {
        name: "ipinfo",
        url: "https://ipinfo.io/json",
        parser: (d) => d && d.ip,
      },
    ];

    let lastErr = null;
    for (const s of services) {
      try {
        const res = await fetch(s.url, {
          method: "GET",
          headers: { Accept: "application/json" },
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`${s.name} returned ${res.status}`);
        const data = await res.json();
        const ip = s.parser(data);
        if (ip && typeof ip === "string") return ip;
        throw new Error(`${s.name} returned no ip`);
      } catch (err) {
        console.warn(`${s.name} client-ip lookup failed:`, err?.message || err);
        lastErr = err;
        continue;
      }
    }
    console.warn("All client IP services failed", lastErr?.message || lastErr);
    return null;
  }

  /**
   * Gets the current location using the browser's geolocation API.
   * Falls back to IP geolocation if GPS fails.
   * @returns {Promise<Object>} - A promise that resolves to the coordinates { latitude, longitude }.
   */
  static async getLocation() {
    // First try browser geolocation (most accurate)
    if (
      typeof navigator !== "undefined" &&
      navigator.geolocation &&
      this.isSecureContext()
    ) {
      try {
        return await new Promise((resolve, reject) => {
          // Enhanced options for mobile devices, especially Android
          const options = {
            enableHighAccuracy: true, // Use GPS if available
            timeout: 30000, // Increased timeout for mobile devices
            maximumAge: 60000, // Cache location for 1 minute to improve performance
          };

          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude,
                speed: position.coords.speed,
                timestamp: position.timestamp,
                source: "gps", // Indicate this came from GPS/browser geolocation
              });
            },
            async (error) => {
              console.warn(
                `GPS geolocation failed (${error.code}: ${error.message}); no client-side IP lookup will be performed`,
              );
              // Do NOT perform IP geolocation on the client — forward to server instead.
              resolve(null);
            },
            options,
          );
        });
      } catch (gpsError) {
        console.warn(
          "GPS geolocation error; no client-side IP lookup will be performed:",
          gpsError.message,
        );
        return null;
      }
    } else {
      // Browser doesn't support geolocation or not in secure context, use IP fallback
      console.warn(
        "Browser geolocation not available or not in secure context; no client-side IP lookup will be performed",
      );
      return null;
    }
  }

  /**
   * Attaches location data to the provided event details.
   * @param {Object} eventDetails - The original event details (may contain location and client_ip).
   * @param {string} [clientIP] - Optional client IP to use as fallback.
   * @returns {Promise<Object>} - The updated event details with location data.
   */
  static async attachLocation(...args) {
    const { eventDetails, clientIP } = LocationSDK._normalizeOptions(args, [
      "eventDetails",
      "clientIP",
    ]);
    try {
      const result = { ...eventDetails };
      let foundLocation = false;

      // Try to get location (GPS first)
      try {
        const coordinates = await this.getLocation();
        if (coordinates) {
          result.location = coordinates;
          foundLocation = true;
        }
      } catch (e) {
        // GPS failed, continue to fallbacks
      }

      // If GPS failed, use eventDetails.location if provided
      if (!foundLocation && eventDetails.location) {
        result.location = eventDetails.location;
        foundLocation = true;
      }

      // Handle client IP: try auto-detect first (overrides user-provided)
      try {
        if (typeof window !== "undefined") {
          const ip = await this.getClientIP();
          if (ip) {
            result.client_ip = ip; // Override with auto-detected
          } else if (clientIP && typeof clientIP === "string") {
            result.client_ip = clientIP; // Fallback to parameter
          } else if (
            eventDetails.client_ip &&
            typeof eventDetails.client_ip === "string"
          ) {
            result.client_ip = eventDetails.client_ip; // Fallback to eventDetails
          }
        } else if (clientIP && typeof clientIP === "string") {
          result.client_ip = clientIP;
        } else if (
          eventDetails.client_ip &&
          typeof eventDetails.client_ip === "string"
        ) {
          result.client_ip = eventDetails.client_ip;
        }
      } catch (e) {
        // Auto-detect failed, use fallbacks
        if (clientIP && typeof clientIP === "string") {
          result.client_ip = clientIP;
        } else if (
          eventDetails.client_ip &&
          typeof eventDetails.client_ip === "string"
        ) {
          result.client_ip = eventDetails.client_ip;
        }
      }

      return result;
    } catch (error) {
      throw new Error(`Failed to get location: ${error.message}`);
    }
  }

  /**
   * Sends a request to the specified URL with the provided headers and data.
   * @param {string} url - The endpoint URL.
   * @param {Object} headers - The request headers.
   * @param {Object} data - The request data.
   * @param {string} [environment="staging"] - Optional environment parameter ("staging" or "production").
   * @returns {Promise<Object>} - The response data.
   */
  static async sendEvent(...args) {
    const { url, headers, data, environment, clientIP } =
      LocationSDK._normalizeOptions(args, [
        "url",
        "headers",
        "data",
        "environment",
        "clientIP",
      ]);
    try {
      const baseUrl =
        environment === "production"
          ? "https://ermc-api.nrs.gov.ng/integration-gateway/external"
          : "https://staging-api.ermc.nrs.gov.ng/integration-gateway/external";

      // Replace the placeholder baseUrl in the url with the actual baseUrl
      const actualUrl = url.replace("{baseUrl}", baseUrl);

      const updatedData = await this.attachLocation(data, clientIP);

      const response = await fetch(actualUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(updatedData),
      });

      if (!response.ok) {
        throw new Error(`Failed to send event: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Error sending event: ${error.message}`);
    }
  }

  /**
   * Reports a bet event.
   * @param {Object} options - The options object.
   * @param {string} options.apiKey - The API key.
   * @param {Object} options.eventDetails - The event details.
   * @param {string} options.eventDetails.bet_id - The unique ID of the bet.
   * @param {Object} options.eventDetails.user - The player object (user_id, first_name, last_name, resident_address, resident_lga, resident_state, is_resident, nin, tax_id).
   * @param {number} options.eventDetails.stake_amount - The amount staked on the bet.
   * @param {string} [options.eventDetails.round_id] - Optional round ID.
   * @param {string} [options.eventDetails.session_id] - Optional session ID.
   * @param {number} [options.eventDetails.odds] - The odds of the bet (required if game_category is 'sports').
   * @param {number} [options.eventDetails.expected_payout] - The expected payout for the bet (required if game_category is 'sports').
   * @param {string} [options.eventDetails.game_category] - Optional game category. Allowed values: 'sports', 'casino', 'lottery'. Defaults to 'sports'.
   * @param {string} [options.eventDetails.game_type] - Optional game type (free-form string).
   * @param {Object} [options.eventDetails.location] - Optional location object (will be overridden by GPS if available).
   * @param {string} [options.eventDetails.client_ip] - Optional client IP (will be overridden by auto-detected IP if available).
   * @param {Object} [options.eventDetails.metadata] - Optional additional metadata for the bet.
   * @param {string} [options.environment="staging"] - Optional environment parameter ("staging" or "production").
   * @param {string} [options.clientIP] - Optional client IP string (will be overridden by auto-detected IP if available).
   * @returns {Promise<Object>} - The response data from the API.
   */
  static async reportBetEvent(options) {
    if (!options || typeof options !== "object") {
      throw new Error(
        "reportBetEvent requires a single options object: { apiKey, eventDetails, environment?, clientIP? }",
      );
    }
    const {
      apiKey,
      eventDetails,
      environment = "staging",
      clientIP = null,
    } = options;

    if (!apiKey) throw new Error("reportBetEvent: apiKey is required");
    if (!eventDetails)
      throw new Error("reportBetEvent: eventDetails is required");

    LocationSDK.validateBetEvent(eventDetails);

    const url = `{baseUrl}/report-bet-event`;
    const headers = {
      "x-api-key": apiKey,
      "Content-Type": "application/json",
    };

    return this.sendEvent(url, headers, eventDetails, environment, clientIP);
  }

  /**
   * Reports a withdrawal event.
   * @param {Object} options - The options object.
   * @param {string} options.apiKey - The API key.
   * @param {Object} options.eventDetails - The event details.
   * @param {string} options.eventDetails.request_id - The unique ID of the withdrawal request.
   * @param {Object} options.eventDetails.user - The player object (user_id, first_name, last_name, resident_address, resident_lga, resident_state, is_resident, nin, tax_id).
   * @param {number} options.eventDetails.amount - The withdrawal amount (must be positive).
   * @param {Object} [options.eventDetails.location] - Optional location object (will be overridden by GPS if available).
   * @param {string} [options.eventDetails.client_ip] - Optional client IP (will be overridden by auto-detected IP if available).
   * @param {Object} [options.eventDetails.metadata] - Optional additional metadata for the withdrawal.
   * @param {string} [options.environment="staging"] - Optional environment parameter ("staging" or "production").
   * @param {string} [options.clientIP] - Optional client IP string (will be overridden by auto-detected IP if available).
   * @returns {Promise<Object>} - The response data from the API.
   */
  static reportWithdrawalEvent(options) {
    if (!options || typeof options !== "object") {
      throw new Error(
        "reportWithdrawalEvent requires a single options object: { apiKey, eventDetails, environment?, clientIP? }",
      );
    }
    const {
      apiKey,
      eventDetails,
      environment = "staging",
      clientIP = null,
    } = options;

    if (!apiKey) throw new Error("reportWithdrawalEvent: apiKey is required");
    if (!eventDetails)
      throw new Error("reportWithdrawalEvent: eventDetails is required");

    LocationSDK.validateWithdrawalEvent(eventDetails);

    const url = `{baseUrl}/report-withdrawal`;
    const headers = {
      "x-api-key": apiKey,
      "Content-Type": "application/json",
    };

    return this.sendEvent(url, headers, eventDetails, environment, clientIP);
  }

  /**
   * Validates the required fields for bet events.
   * @param {Object} eventDetails - The event details.
   */
  static validateBetEvent(eventDetails) {
    // Validate bet_id
    if (!eventDetails.bet_id || typeof eventDetails.bet_id !== "string") {
      throw new Error("Missing required field: bet_id (must be a string)");
    }

    // Validate user object and its required fields
    if (!eventDetails.user || typeof eventDetails.user !== "object") {
      throw new Error(
        "Missing required field: user (must be an object with player details)",
      );
    }
    const requiredPlayerFields = [
      "user_id",
      "first_name",
      "last_name",
      "resident_address",
      "resident_lga",
      "resident_state",
      "is_resident",
      "nin",
    ];
    requiredPlayerFields.forEach((field) => {
      if (!eventDetails.user[field] && eventDetails.user[field] !== false) {
        throw new Error(`Missing required field in user: ${field}`);
      }
    });

    // Validate stake_amount
    if (typeof eventDetails.stake_amount !== "number") {
      throw new Error(
        "Missing required field: stake_amount (must be a number)",
      );
    }

    // Conditional validation: 'sports' category requires odds and expected_payout
    const gameCategory = eventDetails.game_category || "sports";
    const allowedCategories = ["sports", "casino", "lottery"];
    if (!allowedCategories.includes(gameCategory)) {
      throw new Error(
        `Invalid game_category: ${gameCategory}. Allowed values are: ${allowedCategories.join(", ")}`,
      );
    }

    if (gameCategory === "sports") {
      if (typeof eventDetails.odds !== "number") {
        throw new Error(
          "Missing required field: odds (required for sports game_category, must be a number)",
        );
      }
      if (typeof eventDetails.expected_payout !== "number") {
        throw new Error(
          "Missing required field: expected_payout (required for sports game_category, must be a number)",
        );
      }
    }

    // Optional field type validation
    if (
      eventDetails.round_id !== undefined &&
      typeof eventDetails.round_id !== "string"
    ) {
      throw new Error("round_id must be a string if provided");
    }
    if (
      eventDetails.session_id !== undefined &&
      typeof eventDetails.session_id !== "string"
    ) {
      throw new Error("session_id must be a string if provided");
    }
    if (
      eventDetails.game_type !== undefined &&
      typeof eventDetails.game_type !== "string"
    ) {
      throw new Error("game_type must be a string if provided");
    }
    if (
      eventDetails.metadata !== undefined &&
      typeof eventDetails.metadata !== "object"
    ) {
      throw new Error("metadata must be an object if provided");
    }
    if (
      eventDetails.client_ip !== undefined &&
      typeof eventDetails.client_ip !== "string"
    ) {
      throw new Error("client_ip must be a string if provided");
    }
    if (
      eventDetails.location !== undefined &&
      typeof eventDetails.location !== "object"
    ) {
      throw new Error("location must be an object if provided");
    }
  }

  /**
   * Validates the required fields for withdrawal events.
   * @param {Object} eventDetails - The event details.
   */
  static validateWithdrawalEvent(eventDetails) {
    // Validate request_id
    if (
      !eventDetails.request_id ||
      typeof eventDetails.request_id !== "string"
    ) {
      throw new Error("Missing required field: request_id (must be a string)");
    }

    // Validate user object and its required fields
    if (!eventDetails.user || typeof eventDetails.user !== "object") {
      throw new Error(
        "Missing required field: user (must be an object with player details)",
      );
    }
    const requiredPlayerFields = [
      "user_id",
      "first_name",
      "last_name",
      "resident_address",
      "resident_lga",
      "resident_state",
      "is_resident",
      "nin",
    ];
    requiredPlayerFields.forEach((field) => {
      if (!eventDetails.user[field] && eventDetails.user[field] !== false) {
        throw new Error(`Missing required field in user: ${field}`);
      }
    });

    // Validate amount
    if (typeof eventDetails.amount !== "number" || eventDetails.amount <= 0) {
      throw new Error(
        "Missing required field: amount (must be a positive number)",
      );
    }

    // Optional field type validation
    if (
      eventDetails.location !== undefined &&
      typeof eventDetails.location !== "object"
    ) {
      throw new Error("location must be an object if provided");
    }
    if (
      eventDetails.metadata !== undefined &&
      typeof eventDetails.metadata !== "object"
    ) {
      throw new Error("metadata must be an object if provided");
    }
    if (
      eventDetails.client_ip !== undefined &&
      typeof eventDetails.client_ip !== "string"
    ) {
      throw new Error("client_ip must be a string if provided");
    }
  }
}

// Export for both Node.js and browser environments
if (typeof module !== "undefined" && module.exports) {
  module.exports = LocationSDK;
} else if (typeof window !== "undefined") {
  window.LocationSDK = LocationSDK;
}

export default LocationSDK;
